import { useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const formUrl = "https://readdy.ai/api/form/d9spdh94ne17ei0sdllg";

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotVal = (formData.get("phone_alt") as string || "").trim();
    if (honeypotVal) {
      setFormStatus("success");
      return;
    }

    const emailVal = (formData.get("email") as string || "").trim();
    if (!emailVal) {
      setFormError("请填写邮箱地址");
      setFormStatus("error");
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append("name", (formData.get("name") as string || "").trim());
      params.append("email", emailVal);
      params.append("message", (formData.get("message") as string || "").trim());

      const res = await fetch(formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        setFormError(parsed?.meta?.message || parsed?.message || "提交失败，请稍后重试");
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查连接后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                name: "牛奶片库",
                url: "https://naipianku.com",
                description: "牛奶片库 — 精选牛奶片大全，品质甄选一站购齐。汇聚海量优质牛奶片产品，提供专业评测与选购指南。",
                telephone: "400-888-9999",
                email: "hello@naipianku.com",
                address: {
                  "@type": "PostalAddress",
                  addressLocality: "上海",
                  addressRegion: "上海",
                  addressCountry: "CN",
                  streetAddress: "浦东新区张江高科技园区",
                },
              },
              {
                "@type": "WebPage",
                name: "关于我们 - 牛奶片库",
                url: "https://naipianku.com/about",
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "首页", item: "https://naipianku.com" },
                  { "@type": "ListItem", position: 2, name: "关于我们" },
                ],
              },
            ],
          }),
        }}
      />

      <div className="min-h-screen pt-20 pb-14 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="py-4">
            <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
          </div>

          <section className="py-10 md:py-14">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 text-center" style={{ fontFamily: "var(--font-heading)" }}>
              关于牛奶片库
            </h1>
            <p className="text-sm md:text-base text-foreground-600 text-center max-w-2xl mx-auto mb-12">
              致力于成为国内最专业、最全面的牛奶片产品信息与选购指导平台
            </p>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-6">
              <h2 className="text-lg md:text-xl font-semibold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                品牌介绍
              </h2>
              <p>
                牛奶片库成立于2023年，是国内首个专注于<strong>牛奶片</strong>品类的内容平台。创始团队由营养学专家、食品工程专业人员和资深消费品编辑组成，我们深知消费者在面对琳琅满目的牛奶片产品时的选择困难。
              </p>
              <p>
                目前，牛奶片库已收录超过50个品牌的<strong>牛奶片</strong>产品信息，覆盖<strong>儿童牛奶片</strong>、<strong>成人牛奶片</strong>、<strong>进口牛奶片</strong>、<strong>高钙牛奶片</strong>、<strong>无糖牛奶片</strong>和<strong>益生菌牛奶片</strong>六大品类。累计发布200余篇专业评测文章，获得超过10,000名真实用户的认可与好评。
              </p>
              <p>
                我们坚持<strong>客观、专业、实用</strong>的原则，每一款产品都经过团队的实测检验，每一篇文章都力求数据详实、观点清晰。牛奶片库不为任何品牌站台，只为您找到最适合的牛奶片。
              </p>

              <h2 className="text-lg md:text-xl font-semibold text-foreground-950 pt-4" style={{ fontFamily: "var(--font-heading)" }}>
                我们的使命
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: "ri-shield-check-line", title: "严选品质", desc: "每款产品均经过团队实测筛选，确保信息真实可靠" },
                  { icon: "ri-lightbulb-line", title: "专业评测", desc: "营养师+食品专家联合测评，提供多维度分析" },
                  { icon: "ri-heart-line", title: "用户至上", desc: "以消费者需求为导向，做最贴近用户的选购指南" },
                ].map((item) => (
                  <div key={item.title} className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                    <div className="w-11 h-11 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                      <i className={`${item.icon} text-xl text-accent-600`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                    <p className="text-xs text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-lg md:text-xl font-semibold text-foreground-950 pt-4" style={{ fontFamily: "var(--font-heading)" }}>
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { name: "刘女士", role: "二胎妈妈", text: "牛奶片库的儿童牛奶片排行真的帮了大忙！按照推荐买了牛哈哈儿童牛奶片，我家两个宝宝都超级爱吃，而且成分真的很干净。" },
                  { name: "张先生", role: "健身爱好者", text: "一直在找高蛋白又不加糖的牛奶片，看了牛奶片库的高钙牛奶片评测后选了萌纯，钙含量高吸收也好，健身后的补给好搭档。" },
                  { name: "王阿姨", role: "退休教师", text: "女儿推荐的牛奶片库，上面关于中老年人补钙的建议非常专业。按照指南选择了合适的牛奶片，坚持吃了两个月，感觉确实不错。" },
                  { name: "赵女士", role: "白领", text: "办公室常备小奶牛成人牛奶片，奶香纯正不腻。牛奶片库的评测很客观，没有一味吹捧贵的产品，推荐的性价比款确实好用。" },
                ].map((review) => (
                  <div key={review.name} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-[12px] text-accent-500"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      "{review.text}"
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-600">{review.name[0]}</span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground-900">{review.name}</p>
                        <p className="text-[11px] text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="text-lg md:text-xl font-semibold text-foreground-950 pt-4" style={{ fontFamily: "var(--font-heading)" }}>
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="text-sm text-foreground-700 mb-6">
                    如果您有任何关于牛奶片产品的疑问、合作意向或意见建议，欢迎随时与我们联系。牛奶片库团队将尽快回复您的每一条消息。
                  </p>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 text-sm text-foreground-700">
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <span>hello@naipianku.com</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-700">
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-primary-600"></i>
                      </div>
                      <span>400-888-9999</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-700">
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600"></i>
                      </div>
                      <span>上海市浦东新区张江高科技园区</span>
                    </div>
                  </div>
                </div>

                <form
                  onSubmit={handleContactSubmit}
                  className="flex flex-col gap-4"
                  data-readdy-form=""
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      rows={4}
                      placeholder="请输入您的留言内容（不超过500字）"
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="website_alt_field"
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-send-plane-line mr-2"></i>发送消息
                  </button>

                  {formStatus === "success" && (
                    <p className="text-sm text-green-600 text-center">
                      <i className="ri-check-line mr-1"></i>消息已发送，我们会尽快回复您！
                    </p>
                  )}
                  {formStatus === "error" && formError && (
                    <p className="text-sm text-red-500 text-center">
                      <i className="ri-error-warning-line mr-1"></i>{formError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}